import React, { memo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useWindowSize } from 'react-use';
import classnames from 'classnames';
import { useScrollbarStyles } from 'modules/common/styles';
import { CopyrightFooter } from 'modules/common/components/CopyrightFooter';
import { useSidePanelContext } from 'modules/rootPage/SidePanelContext';
import { CollectorInstallStatus, Maintainer, Setting } from 'graphql/types';
import { AllCollectorsListPage } from './AllCollectorsListPage';
import { CollectorHeader } from './CollectorHeader';
import { useAllCollectors } from './hooks/useAllCollectors';
import { useCollectorSettingsUnified } from './hooks/useCollectorSettingsUnified';
import { UnifiedCollectorListPageSkeleton } from './UnifiedCollectorListPageSkeleton';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    height: 'inherit',
    overflowX: 'hidden',
    overflowY: 'auto',
    background: theme.palette.information.pageBackground,
    width: ({ width }: { width: number }) => width,
    display: 'flex',
    flexDirection: 'column'
  },
  copyright: {
    marginTop: theme.spacing(4)
  }
}));
export type CollectorInstallInfo = {
  userInstallationDetails: Maintainer[];
  userInstallationDate: string;
  userSettings: Setting[];
  userInstallStatus: CollectorInstallStatus;
  tenantInstallationDetails: Maintainer[];
  tenantInstallationDate: string;
  tenantSettings: Setting[];
  tenantInstallStatus: CollectorInstallStatus;
};

type AllCollectorsTabProps = {
  hasError: boolean;
};

export const AllCollectorsTab: React.FC<AllCollectorsTabProps> = memo(
  ({ hasError }: AllCollectorsTabProps) => {
    const { pinned = false } = useSidePanelContext();
    const { width } = useWindowSize();

    const [searchValue, setSearchValue] = React.useState('');
    const classes = useStyles({
      width: pinned ? width - 240 : width - 64
    });
    const classesScrollbar = useScrollbarStyles();
    const { data, refetch } = useAllCollectors();
    const {
      data: userSettingData,
      refetch: settingRefetch
    } = useCollectorSettingsUnified();

    if (!data || !userSettingData) {
      return (
        <div data-testid="unified-collector-list-page-skeleton">
          <UnifiedCollectorListPageSkeleton />
        </div>
      );
    }
    return (
      <div className={classnames(classes.root, classesScrollbar.scrollbar)}>
        <CollectorHeader setSearchValue={setSearchValue} />
        <AllCollectorsListPage
          searchValue={searchValue}
          data={data}
          refetch={refetch}
          hasError={hasError}
          userSettingData={userSettingData}
          settingRefetch={settingRefetch}
        />

        <CopyrightFooter className={classes.copyright}></CopyrightFooter>
      </div>
    );
  }
);
AllCollectorsTab.displayName = 'AllCollectorsTab';
